import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Metric } from '../components/SharedComponents';
import { getPersonalData, getDailyUnits } from '../qlik';

// Personal/löner — endast admin (fliken renderas bara för admins i LedningBlock,
// och backend-callablen verifierar admin på servern innan data lämnas ut).
const COLOR = '#1B2A4A';
const DOW_SV = ['mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön'];
// Schablon: arbetsgivaravgift 31,42% → brutto = kostnad / 1,3142
const AGA = 0.3142;

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const kr = (v) => (v == null || isNaN(v)) ? '–' : Math.round(v).toLocaleString('sv-SE') + ' kr';
const tim1 = (v) => v.toFixed(1).replace('.', ',');
const fmtDag = (iso) => { const d = fromISO(iso); return `${DOW_SV[(d.getDay() + 6) % 7]} ${d.getDate()}/${d.getMonth() + 1}`; };

// Avdelningsfärger — samma palett som Ledning-blocket, för snabb visuell sortering
const enhetColor = (e) => {
  if (!e || e === 'Saknas') return '#8A8A8A';
  if (e.startsWith('Hotell') || e === 'Hotellrestaurangen') return '#1B2A4A';
  if (e === 'Brasserie Kerstin' || e === 'Vävstugan')       return '#7A3B1E';
  if (e === 'Knallen' || e === 'Kariknallen')               return '#6B4E2E';
  if (e === 'Sportcenter')                                   return '#1A6E3C';
  if (e === 'Stugservice')                                   return '#3B6D8A';
  if (e === 'OH' || e === 'Event')                           return '#A32D2D';
  return '#8A8A8A';
};

// Anställningsform per person (planeringsverktyg) — sparas i intel_config
const KAT = {
  fast:   { label: 'Fast',   farg: '#1D6A5F' },
  sasong: { label: 'Säsong', farg: '#C98A2E' },
};
const MANADER_SV = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const sel = { fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)' };
const tabBtn = (active) => ({ background: active ? COLOR : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '5px 13px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' });
const miniBtn = (active) => ({ ...tabBtn(active), padding: '4px 11px', fontSize: 11.5 });

// rows: [[datum, namn, enhet, transtyp, lon, tim]] → objekt
const mapRows = (rows) => (rows || []).map(([datum, namn, enhet, transtyp, lon, tim]) => ({
  datum, namn, enhet, fast: transtyp === 'Fasta lönekostnader', lon, tim,
}));

// Klickbar tabellrubrik med sorteringspil
function SortTh({ id, label, sortKey, sortDir, onSort, right }) {
  const active = sortKey === id;
  return (
    <span onClick={() => onSort(id)}
      style={{ cursor: 'pointer', userSelect: 'none', textAlign: right ? 'right' : 'left', color: active ? COLOR : undefined, fontWeight: active ? 700 : undefined }}
      title="Klicka för att sortera">
      {label}{active ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
    </span>
  );
}

export default function PersonalTab() {
  const todayISO = toISO(new Date());
  const monthStart = todayISO.slice(0, 8) + '01';

  const [mode, setMode] = useState('dag');           // 'dag' | 'manad' | 'person'
  const [manadVal, setManadVal] = useState(todayISO.slice(0, 7));
  const [doldaEnheter, setDoldaEnheter] = useState(new Set()); // urklickade avdelningar
  const [kategorier, setKategorier] = useState({});   // namn → 'fast' | 'sasong'
  const [dagDatum, setDagDatum] = useState(todayISO);
  const [pFrom, setPFrom] = useState(monthStart);
  const [pTo, setPTo] = useState(todayISO);
  const [valdPerson, setValdPerson] = useState(null);
  const [sok, setSok] = useState('');
  const [persVy, setPersVy] = useState('person');    // dag-tabellen: 'person' | 'avdelning'
  const [sortKey, setSortKey] = useState('lon');
  const [sortDir, setSortDir] = useState('desc');

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [units, setUnits] = useState(null);          // daily_units (omsättning per enhet/dag, från cachen)

  // Månadens intervall (kapas vid dagens datum för innevarande månad)
  const manadFrom = `${manadVal}-01`;
  const manadTo = (() => {
    const [y, m] = manadVal.split('-').map(Number);
    const sista = `${manadVal}-${pad(new Date(y, m, 0).getDate())}`;
    return sista > todayISO ? todayISO : sista;
  })();

  const from = mode === 'dag' ? dagDatum : mode === 'manad' ? manadFrom : (pFrom <= pTo ? pFrom : pTo);
  const to   = mode === 'dag' ? dagDatum : mode === 'manad' ? manadTo   : (pFrom <= pTo ? pTo : pFrom);

  useEffect(() => {
    let stale = false;
    setLoading(true); setErr('');
    getPersonalData(from, to)
      .then(d => { if (!stale) setRows(mapRows(d.rows)); })
      .catch(e => { if (!stale) { setRows([]); setErr(e?.message || 'Kunde inte hämta'); } })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [from, to]);

  useEffect(() => { getDailyUnits().then(setUnits).catch(() => setUnits([])); }, []);

  // Anställningsformer (fast/säsong) — laddas en gång, sparas vid ändring
  useEffect(() => {
    getDoc(doc(db, 'intel_config', 'personal_kategorier'))
      .then((s) => setKategorier(s.exists() ? (s.data().personer || {}) : {}))
      .catch(() => {});
  }, []);

  const sattKategori = async (namn) => {
    const nu = kategorier[namn];
    const ny = nu === 'fast' ? 'sasong' : nu === 'sasong' ? null : 'fast';
    const nya = { ...kategorier };
    if (ny) nya[namn] = ny; else delete nya[namn];
    setKategorier(nya);
    try {
      await setDoc(doc(db, 'intel_config', 'personal_kategorier'), {
        personer: nya, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || '',
      });
    } catch (e) { console.error('spara kategori:', e); }
  };

  const KatBadge = ({ namn }) => {
    const k = kategorier[namn];
    return (
      <button onClick={(e) => { e.stopPropagation(); sattKategori(namn); }}
        title={k ? `${KAT[k].label} — klicka för att ändra` : 'Okategoriserad — klicka för att sätta Fast'}
        style={{ background: k ? `${KAT[k].farg}1E` : 'transparent', color: k ? KAT[k].farg : 'var(--text-secondary)',
          border: `1px solid ${k ? `${KAT[k].farg}66` : 'var(--border)'}`, borderRadius: 20, padding: '1px 8px',
          fontSize: 10, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {k ? KAT[k].label : 'Sätt'}
      </button>
    );
  };

  const onSort = (id) => {
    if (sortKey === id) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(id); setSortDir(id === 'namn' || id === 'enhet' ? 'asc' : 'desc'); }
  };

  const RAlla = useMemo(() => rows || [], [rows]);
  const allaEnheter = useMemo(() => [...new Set(RAlla.map(r => r.enhet))].sort((a, b) => a.localeCompare(b, 'sv')), [RAlla]);
  // Urklickade avdelningar räknas bort ur ALLA sammanställningar
  const R = useMemo(() => RAlla.filter(r => !doldaEnheter.has(r.enhet)), [RAlla, doldaEnheter]);
  const pk    = useMemo(() => R.filter(r => !r.fast), [R]);
  const fasta = useMemo(() => R.filter(r => r.fast), [R]);

  // ── MÅNAD-LÄGET ──
  const manadData = useMemo(() => {
    if (mode !== 'manad') return null;
    const pkSum = pk.reduce((s, r) => s + r.lon, 0);
    const fastSum = fasta.reduce((s, r) => s + r.lon, 0);
    const timSum = pk.reduce((s, r) => s + r.tim, 0);

    const byPerson = {};
    for (const r of pk) {
      const p = byPerson[r.namn] || (byPerson[r.namn] = { namn: r.namn, lon: 0, tim: 0, dagar: new Set(), enheter: new Set() });
      p.lon += r.lon; p.tim += r.tim; p.dagar.add(r.datum); p.enheter.add(r.enhet);
    }
    const q = sok.trim().toLowerCase();
    const personer = Object.values(byPerson)
      .filter(p => !q || p.namn.toLowerCase().includes(q))
      .sort((a, b) => b.lon - a.lon);

    // Fast vs säsong — planeringens kärna (styrfilens fasta löner läggs på "fast")
    const perKat = { fast: fastSum, sasong: 0, okat: 0 };
    const antalKat = { fast: 0, sasong: 0, okat: 0 };
    for (const p of Object.values(byPerson)) {
      const k = kategorier[p.namn] || 'okat';
      perKat[k] += p.lon; antalKat[k] += 1;
    }

    const byEnhet = {};
    for (const r of R) {
      const e = byEnhet[r.enhet] || (byEnhet[r.enhet] = { lon: 0, tim: 0, fast: 0 });
      e.lon += r.lon; e.tim += r.tim; if (r.fast) e.fast += r.lon;
    }
    const revByEnhet = {};
    for (const u of (units || [])) {
      if (u.datum < from || u.datum > to) continue;
      revByEnhet[u.enhet] = (revByEnhet[u.enhet] || 0) + (u.intakt || 0);
    }
    const enheter = [...new Set([...Object.keys(byEnhet), ...Object.keys(revByEnhet)])]
      .filter(e => !doldaEnheter.has(e))
      .map(e => {
        const lon = byEnhet[e]?.lon || 0, tim = byEnhet[e]?.tim || 0, oms = revByEnhet[e] || 0;
        return { enhet: e, lon, tim, oms, pct: oms > 1000 && lon > 0 ? Math.round(lon / oms * 100) : null };
      })
      .filter(r => r.lon !== 0 || r.oms !== 0)
      .sort((a, b) => b.lon - a.lon);

    return {
      pkSum, fastSum, timSum, tot: pkSum + fastSum,
      personer, antalPersoner: Object.keys(byPerson).length, perKat, antalKat,
      enheter, maxLon: Math.max(...enheter.map(e => e.lon), 1),
      omsTot: enheter.reduce((s, e) => s + e.oms, 0),
      lonTot: enheter.reduce((s, e) => s + e.lon, 0),
    };
  }, [mode, R, pk, fasta, sok, kategorier, units, from, to, doldaEnheter]);

  // ── DAG-LÄGET ──
  const dagData = useMemo(() => {
    if (mode !== 'dag') return null;
    const pkSum = pk.reduce((s, r) => s + r.lon, 0);
    const fastSum = fasta.reduce((s, r) => s + r.lon, 0);
    const timSum = pk.reduce((s, r) => s + r.tim, 0);
    const personer = new Set(pk.map(r => r.namn)).size;
    // per avdelning-staplar (stämplat + fast ihop)
    const byEnhet = {};
    for (const r of R) {
      if (!byEnhet[r.enhet]) byEnhet[r.enhet] = { enhet: r.enhet, lon: 0, tim: 0, fast: 0 };
      byEnhet[r.enhet].lon += r.lon; byEnhet[r.enhet].tim += r.tim;
      if (r.fast) byEnhet[r.enhet].fast += r.lon;
    }
    const enheter = Object.values(byEnhet).sort((a, b) => b.lon - a.lon);
    const maxEnhet = Math.max(...enheter.map(e => e.lon), 1);
    // personrader, sök-filtrerade + sorterbara
    const q = sok.trim().toLowerCase();
    const withKrh = pk.map(r => ({ ...r, krh: r.tim > 0 ? r.lon / r.tim : 0 }));
    const cmp = (a, b) => {
      const dir = sortDir === 'desc' ? -1 : 1;
      if (sortKey === 'namn')  return dir * a.namn.localeCompare(b.namn, 'sv');
      if (sortKey === 'enhet') return dir * (a.enhet.localeCompare(b.enhet, 'sv') || b.lon - a.lon);
      return dir * ((a[sortKey] || 0) - (b[sortKey] || 0));
    };
    const persRader = withKrh.filter(r => !q || r.namn.toLowerCase().includes(q)).sort(cmp);
    // per avdelning-vy: personerna grupperade per avdelning med delsummor
    // (samma sök-filter som person-vyn; grupper + personer sorterade på lön)
    const byAvd = {};
    for (const r of persRader) {
      if (!byAvd[r.enhet]) byAvd[r.enhet] = { enhet: r.enhet, rader: [], tim: 0, lon: 0 };
      byAvd[r.enhet].rader.push(r); byAvd[r.enhet].tim += r.tim; byAvd[r.enhet].lon += r.lon;
    }
    const avdGrupper = Object.values(byAvd)
      .map(g => ({ ...g, rader: [...g.rader].sort((a, b) => b.lon - a.lon) }))
      .sort((a, b) => b.lon - a.lon);
    // omsättning per avdelning (från daily_units-cachen) för matchning mot lön
    const revByEnhet = {};
    for (const u of (units || [])) {
      if (u.datum !== dagDatum) continue;
      revByEnhet[u.enhet] = (revByEnhet[u.enhet] || 0) + (u.intakt || 0);
    }
    const allaEnheter = [...new Set([...Object.keys(revByEnhet), ...Object.keys(byEnhet)])];
    const omsRader = allaEnheter
      .map(e => {
        const oms = revByEnhet[e] || 0;
        const lon = byEnhet[e]?.lon || 0;
        return { enhet: e, oms, lon, pct: oms > 1000 && lon > 0 ? Math.round(lon / oms * 100) : null };
      })
      .filter(r => r.oms !== 0 || r.lon !== 0)
      .sort((a, b) => b.oms - a.oms);
    const omsTot = omsRader.reduce((s, r) => s + r.oms, 0);
    const lonTot = omsRader.reduce((s, r) => s + r.lon, 0);
    return { pkSum, fastSum, timSum, personer, enheter, maxEnhet, persRader, avdGrupper,
             omsRader, omsTot, lonTot, tot: pkSum + fastSum };
  }, [mode, R, pk, fasta, sok, sortKey, sortDir, units, dagDatum]);

  // ── PERSON-LÄGET ──
  const persData = useMemo(() => {
    if (mode !== 'person') return null;
    const byPerson = {};
    for (const r of pk) {
      if (!byPerson[r.namn]) byPerson[r.namn] = { namn: r.namn, lon: 0, tim: 0, dagar: new Set(), enheter: new Set() };
      const p = byPerson[r.namn];
      p.lon += r.lon; p.tim += r.tim; p.dagar.add(r.datum); p.enheter.add(r.enhet);
    }
    const q = sok.trim().toLowerCase();
    const lista = Object.values(byPerson)
      .filter(p => !q || p.namn.toLowerCase().includes(q))
      .sort((a, b) => b.lon - a.lon);
    const listSum = { lon: lista.reduce((s, p) => s + p.lon, 0), tim: lista.reduce((s, p) => s + p.tim, 0) };
    const vald = valdPerson && byPerson[valdPerson] ? byPerson[valdPerson] : null;
    const valdRader = vald ? pk.filter(r => r.namn === valdPerson).sort((a, b) => a.datum < b.datum ? 1 : -1) : [];
    return { lista, listSum, vald, valdRader };
  }, [mode, pk, sok, valdPerson]);

  return (
    <>
      {/* Läge + period */}
      <div className="card" data-noexport style={{ padding: '12px 14px' }}>
       <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setMode('dag')} style={tabBtn(mode === 'dag')}>Dag</button>
          <button onClick={() => setMode('manad')} style={tabBtn(mode === 'manad')}>Månad</button>
          <button onClick={() => setMode('person')} style={tabBtn(mode === 'person')}>Person</button>
        </div>
        <div style={{ width: 1, height: 22, background: 'var(--border)' }} />
        {mode === 'dag' ? (
          <input type="date" style={sel} max={todayISO} value={dagDatum} onChange={e => e.target.value && setDagDatum(e.target.value)} />
        ) : mode === 'manad' ? (
          <input type="month" style={sel} max={todayISO.slice(0, 7)} value={manadVal} onChange={e => e.target.value && setManadVal(e.target.value)} />
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontFamily: 'system-ui,sans-serif', fontSize: 12 }}>
            <input type="date" style={sel} max={todayISO} value={pFrom} onChange={e => e.target.value && setPFrom(e.target.value)} />
            <span>till</span>
            <input type="date" style={sel} max={todayISO} value={pTo} onChange={e => e.target.value && setPTo(e.target.value)} />
          </div>
        )}
        <input type="search" placeholder="Sök namn…" value={sok} onChange={e => setSok(e.target.value)}
          style={{ ...sel, minWidth: 160 }} />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>🔒 Endast admin</span>
       </div>

       {/* Avdelningsfilter — klicka ur det du inte vill ha med i sammanställningen */}
       {allaEnheter.length > 1 && (
         <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
           <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', marginRight: 3 }}>Avdelningar:</span>
           {allaEnheter.map((e) => {
             const av = doldaEnheter.has(e);
             return (
               <button key={e} onClick={() => setDoldaEnheter((p) => { const n = new Set(p); if (av) n.delete(e); else n.add(e); return n; })}
                 title={av ? 'Klicka för att räkna med igen' : 'Klicka för att räkna bort ur sammanställningen'}
                 style={{ background: av ? 'transparent' : `${enhetColor(e)}18`, color: av ? 'var(--text-secondary)' : enhetColor(e),
                   border: `1px solid ${av ? 'var(--border)' : `${enhetColor(e)}55`}`, borderRadius: 20, padding: '3px 10px',
                   fontSize: 11, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', fontWeight: av ? 400 : 600,
                   textDecoration: av ? 'line-through' : 'none', opacity: av ? 0.55 : 1 }}>
                 {e}
               </button>
             );
           })}
           {doldaEnheter.size > 0 && (
             <button onClick={() => setDoldaEnheter(new Set())}
               style={{ background: 'transparent', border: 'none', color: COLOR, fontSize: 11, cursor: 'pointer', fontFamily: 'system-ui,sans-serif', textDecoration: 'underline' }}>
               visa alla
             </button>
           )}
         </div>
       )}
      </div>

      {loading && <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Hämtar lönedata från Qlik…</div></div>}
      {err && !loading && <div className="card"><div style={{ fontSize: 13, color: '#A32D2D', fontFamily: 'system-ui,sans-serif' }}>Fel: {err}</div></div>}

      {/* ── DAG ── */}
      {!loading && !err && mode === 'dag' && dagData && (
        <>
          <div className="grid-4">
            <Metric label={`Total lönekostnad — ${fmtDag(dagDatum)}`} value={kr(dagData.tot)}
              sub={`${dagData.personer} personer stämplade`} />
            <Metric label="Stämplade löner (Personalkollen)" value={kr(dagData.pkSum)}
              sub={dagData.timSum > 0 ? `${tim1(dagData.timSum)} timmar` : '–'} />
            <Metric label="Fasta löner (styrfil)" value={kr(dagData.fastSum)}
              sub="Månadsbelopp utspritt per dag" />
            <Metric label="Snittkostnad / timme" value={dagData.timSum > 0 ? kr(dagData.pkSum / dagData.timSum) : '–'}
              sub="Stämplade löner ÷ timmar" />
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-title">Per avdelning — {fmtDag(dagDatum)}</div>
              {dagData.enheter.map((e, i) => (
                <div key={i} style={{ marginBottom: 8, fontFamily: 'system-ui,sans-serif' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 3 }}>
                    <span>{e.enhet}{e.fast > 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}> (varav fast {kr(e.fast)})</span>}</span>
                    <span style={{ fontWeight: 600 }}>{kr(e.lon)}{e.tim > 0 && <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {tim1(e.tim)} h</span>}</span>
                  </div>
                  <div style={{ height: 5, background: 'var(--panel2)', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${Math.round(e.lon / dagData.maxEnhet * 100)}%`, background: enhetColor(e.enhet), borderRadius: 3 }} />
                  </div>
                </div>
              ))}
              {dagData.enheter.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen lönekostnad denna dag.</div>}
            </div>

            <div className="card">
              <div className="card-title">Fasta löner (styrfil) — per avdelning</div>
              <div className="period-table">
                <div className="period-header" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                  <span>Avdelning</span><span style={{ textAlign: 'right' }}>Kr / dag</span>
                </div>
                {fasta.sort((a, b) => b.lon - a.lon).map((r, i) => (
                  <div key={i} className="period-row" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                    <span>{r.enhet}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>{kr(r.lon)}</span>
                  </div>
                ))}
                {fasta.length > 0 && (
                  <div className="period-row" style={{ gridTemplateColumns: '1.4fr 1fr', fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <span>SUMMA</span>
                    <span style={{ textAlign: 'right' }}>{kr(dagData.fastSum)}</span>
                  </div>
                )}
              </div>
              <div className="note">Styrfilen: personer som inte stämplar (fasta månadslöner), utspridda per dag och bokförda per avdelning. Personfördelning kan visas när styrfilens personkolumn läses in i Qlik.</div>
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                Stämplade löner {persVy === 'person' ? 'per person' : 'per avdelning'} — {fmtDag(dagDatum)}{sok.trim() && persVy === 'person' ? ` · filter "${sok.trim()}"` : ''}
              </div>
              <div style={{ display: 'flex', gap: 5 }}>
                <button onClick={() => setPersVy('person')} style={miniBtn(persVy === 'person')}>Per person</button>
                <button onClick={() => setPersVy('avdelning')} style={miniBtn(persVy === 'avdelning')}>Per avdelning</button>
              </div>
            </div>

            {persVy === 'person' ? (
              <div className="period-table">
                <div className="period-header period-row-5">
                  <SortTh id="namn"  label="Namn"      sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh id="enhet" label="Avdelning" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                  <SortTh id="tim"   label="Timmar"    sortKey={sortKey} sortDir={sortDir} onSort={onSort} right />
                  <SortTh id="lon"   label="Lönekostnad" sortKey={sortKey} sortDir={sortDir} onSort={onSort} right />
                  <SortTh id="krh"   label="Kr / tim"  sortKey={sortKey} sortDir={sortDir} onSort={onSort} right />
                </div>
                {dagData.persRader.map((r, i) => (
                  <div key={i} className="period-row period-row-5" style={{ cursor: 'pointer' }}
                    onClick={() => { setValdPerson(r.namn); setMode('person'); setSok(''); }}
                    title="Visa personens period">
                    <span style={{ fontWeight: 600, color: COLOR }}>{r.namn}</span>
                    <span style={{ color: enhetColor(r.enhet), fontWeight: 500 }}>{r.enhet}</span>
                    <span style={{ textAlign: 'right' }}>{r.tim > 0 ? r.tim.toFixed(2).replace('.', ',') : '–'}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>{kr(r.lon)}</span>
                    <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{r.krh > 0 ? Math.round(r.krh).toLocaleString('sv-SE') : '–'}</span>
                  </div>
                ))}
                {dagData.persRader.length > 0 && (
                  <div className="period-row period-row-5" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <span>SUMMA</span><span />
                    <span style={{ textAlign: 'right' }}>{tim1(dagData.persRader.reduce((s, r) => s + r.tim, 0))}</span>
                    <span style={{ textAlign: 'right' }}>{kr(dagData.persRader.reduce((s, r) => s + r.lon, 0))}</span>
                    <span />
                  </div>
                )}
                {dagData.persRader.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '8px 0' }}>Inga stämplade löner {sok.trim() ? 'som matchar sökningen' : 'denna dag'}.</div>}
              </div>
            ) : (
              <div className="period-table">
                <div className="period-header period-row-5">
                  <span>Namn</span>
                  <span>Avdelning</span>
                  <span style={{ textAlign: 'right' }}>Timmar</span>
                  <span style={{ textAlign: 'right' }}>Lönekostnad</span>
                  <span style={{ textAlign: 'right' }}>Kr / tim</span>
                </div>
                {dagData.avdGrupper.map((g, gi) => {
                  const c = enhetColor(g.enhet);
                  return (
                  <React.Fragment key={g.enhet}>
                    {g.rader.map((r, i) => (
                      <div key={i} className="period-row period-row-5"
                        style={{ cursor: 'pointer', borderLeft: `3px solid ${c}`, paddingLeft: 8,
                                 background: i % 2 === 1 ? `${c}08` : 'transparent',
                                 ...(gi > 0 && i === 0 ? { marginTop: 10 } : {}) }}
                        onClick={() => { setValdPerson(r.namn); setMode('person'); setSok(''); }}
                        title="Visa personens period">
                        <span style={{ fontWeight: 600, color: COLOR }}>{r.namn}</span>
                        <span style={{ color: c, fontWeight: 500 }}>{r.enhet}</span>
                        <span style={{ textAlign: 'right' }}>{r.tim > 0 ? r.tim.toFixed(2).replace('.', ',') : '–'}</span>
                        <span style={{ textAlign: 'right', fontWeight: 600 }}>{kr(r.lon)}</span>
                        <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{r.krh > 0 ? Math.round(r.krh).toLocaleString('sv-SE') : '–'}</span>
                      </div>
                    ))}
                    <div className="period-row period-row-5"
                      style={{ fontWeight: 700, borderTop: 'none', borderBottom: 'none', borderLeft: `3px solid ${c}`,
                               paddingLeft: 8, background: `${c}16`, borderRadius: '0 6px 6px 0' }}>
                      <span style={{ color: c }}>Summa {g.enhet}</span><span />
                      <span style={{ textAlign: 'right' }}>{tim1(g.tim)}</span>
                      <span style={{ textAlign: 'right' }}>{kr(g.lon)}</span>
                      <span style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{g.tim > 0 ? Math.round(g.lon / g.tim).toLocaleString('sv-SE') : '–'}</span>
                    </div>
                  </React.Fragment>
                  );
                })}
                {dagData.avdGrupper.length > 0 && (
                  <div className="period-row period-row-5" style={{ fontWeight: 700, borderTop: '3px double var(--border)', marginTop: 10 }}>
                    <span>TOTALT</span>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{dagData.personer} pers</span>
                    <span style={{ textAlign: 'right' }}>{tim1(dagData.persRader.reduce((s, r) => s + r.tim, 0))}</span>
                    <span style={{ textAlign: 'right' }}>{kr(dagData.persRader.reduce((s, r) => s + r.lon, 0))}</span>
                    <span style={{ textAlign: 'right' }}>{dagData.timSum > 0 ? Math.round(dagData.pkSum / dagData.timSum).toLocaleString('sv-SE') : '–'}</span>
                  </div>
                )}
                {dagData.avdGrupper.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '8px 0' }}>Inga stämplade löner {sok.trim() ? 'som matchar sökningen' : 'denna dag'}.</div>}
              </div>
            )}
            <div className="note">Källa: Qlik · Personalkollen (stämpelklocka). Lönekostnad = arbetsgivarens kostnad. {persVy === 'person' ? 'Klicka på rubrikerna för att sortera · klicka på en rad för personens period. En person kan ha flera rader (olika avdelningar samma dag).' : 'Fasta löner (styrfil) ingår inte — de har eget kort ovan.'} "Saknas" = oschemalagt pass — flyttas till rätt avdelning när driftansvarig registrerar passet.</div>
          </div>

          {/* Omsättning vs lönekostnad per avdelning */}
          <div className="card">
            <div className="card-title">Omsättning vs lönekostnad per avdelning — {fmtDag(dagDatum)}</div>
            <div className="period-table">
              <div className="period-header period-row-4">
                <span>Avdelning</span>
                <span style={{ textAlign: 'right' }}>Omsättning</span>
                <span style={{ textAlign: 'right' }}>Lönekostnad</span>
                <span style={{ textAlign: 'right' }}>Personal%</span>
              </div>
              {dagData.omsRader.map((r, i) => {
                const pctColor = r.pct == null ? 'var(--text-secondary)' : r.pct <= 35 ? '#3B6D11' : r.pct >= 55 ? '#A32D2D' : '#854F0B';
                return (
                  <div key={i} className="period-row period-row-4">
                    <span>{r.enhet}</span>
                    <span style={{ textAlign: 'right' }}>{r.oms !== 0 ? kr(r.oms) : '–'}</span>
                    <span style={{ textAlign: 'right' }}>{r.lon > 0 ? kr(r.lon) : '–'}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600, color: pctColor }}>{r.pct != null ? `${r.pct}%` : '–'}</span>
                  </div>
                );
              })}
              {dagData.omsRader.length > 0 && (
                <div className="period-row period-row-4" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <span>SUMMA</span>
                  <span style={{ textAlign: 'right' }}>{kr(dagData.omsTot)}</span>
                  <span style={{ textAlign: 'right' }}>{kr(dagData.lonTot)}</span>
                  <span style={{ textAlign: 'right' }}>{dagData.omsTot > 1000 ? `${Math.round(dagData.lonTot / dagData.omsTot * 100)}%` : '–'}</span>
                </div>
              )}
              {dagData.omsRader.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '8px 0' }}>Ingen data denna dag.</div>}
            </div>
            <div className="note">Omsättning från portalens cache (uppdateras var 15:e min) · lönekostnad = stämplat + fast. Personal% visas när omsättningen är meningsfull (&gt;1 tkr). OH saknar omsättning per design.</div>
          </div>
        </>
      )}

      {/* ── MÅNAD ── */}
      {!loading && !err && mode === 'manad' && manadData && (() => {
        const [my, mm] = manadVal.split('-').map(Number);
        const mLbl = `${MANADER_SV[mm - 1]} ${my}`;
        const delvis = manadTo < `${manadVal}-${pad(new Date(my, mm, 0).getDate())}`;
        const katRad = (k, belopp, antal, etikett) => (
          <div key={k} style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${k === 'okat' ? 'var(--border)' : KAT[k].farg}`,
            borderRadius: 8, padding: '8px 11px', fontFamily: 'system-ui,sans-serif' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 700 }}>{etikett}</div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{kr(belopp)}</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
              {manadData.tot > 0 ? Math.round(belopp / manadData.tot * 100) : 0} %{antal != null ? ` · ${antal} pers` : ''}
            </div>
          </div>
        );
        return (
        <>
          <div className="grid-4">
            <Metric label={`Total lönekostnad — ${mLbl}`} value={kr(manadData.tot)}
              sub={`${manadData.antalPersoner} personer${delvis ? ` · t.o.m. ${manadTo.slice(8)}/${mm}` : ''}`} />
            <Metric label="Stämplade löner" value={kr(manadData.pkSum)}
              sub={manadData.timSum > 0 ? `${Math.round(manadData.timSum).toLocaleString('sv-SE')} timmar` : '–'} />
            <Metric label="Fasta löner (styrfil)" value={kr(manadData.fastSum)} sub="Ej personkopplade" />
            <Metric label="Personal% mot omsättning" value={manadData.omsTot > 1000 ? `${Math.round(manadData.lonTot / manadData.omsTot * 100)}%` : '–'}
              sub={manadData.omsTot > 0 ? `Oms ${kr(manadData.omsTot)}` : 'Ingen omsättning'} />
          </div>

          {/* Fast vs säsong — planeringsvyn */}
          <div className="card">
            <div className="card-title">Fast vs säsong — {mLbl}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 10 }}>
              {katRad('fast', manadData.perKat.fast, manadData.antalKat.fast, 'Fast personal')}
              {katRad('sasong', manadData.perKat.sasong, manadData.antalKat.sasong, 'Säsongspersonal')}
              {katRad('okat', manadData.perKat.okat, manadData.antalKat.okat, 'Ej kategoriserad')}
            </div>
            {manadData.perKat.okat > 0 && (
              <div style={{ fontSize: 12, color: '#A36B00', fontFamily: 'system-ui,sans-serif' }}>
                {manadData.antalKat.okat} personer ({kr(manadData.perKat.okat)}) saknar anställningsform — sätt dem i tabellen nedan så blir uppdelningen komplett.
              </div>
            )}
            <div className="note">
              Styrfilens fasta löner räknas alltid som fast personal. Kategorin sätts per person och gäller alla månader — den är ditt eget planeringsval, inget som kommer ur Qlik.
            </div>
          </div>

          {/* Per avdelning */}
          <div className="card">
            <div className="card-title">Per avdelning — {mLbl}</div>
            <div className="period-table">
              <div className="period-header period-row-5">
                <span>Avdelning</span>
                <span style={{ textAlign: 'right' }}>Timmar</span>
                <span style={{ textAlign: 'right' }}>Lönekostnad</span>
                <span style={{ textAlign: 'right' }}>Omsättning</span>
                <span style={{ textAlign: 'right' }}>Personal%</span>
              </div>
              {manadData.enheter.map((e, i) => {
                const pctColor = e.pct == null ? 'var(--text-secondary)' : e.pct <= 35 ? '#3B6D11' : e.pct >= 55 ? '#A32D2D' : '#854F0B';
                return (
                  <div key={i} className="period-row period-row-5">
                    <span style={{ color: enhetColor(e.enhet), fontWeight: 600 }}>{e.enhet}</span>
                    <span style={{ textAlign: 'right' }}>{e.tim > 0 ? Math.round(e.tim).toLocaleString('sv-SE') : '–'}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>{e.lon > 0 ? kr(e.lon) : '–'}</span>
                    <span style={{ textAlign: 'right' }}>{e.oms !== 0 ? kr(e.oms) : '–'}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600, color: pctColor }}>{e.pct != null ? `${e.pct}%` : '–'}</span>
                  </div>
                );
              })}
              {manadData.enheter.length > 0 && (
                <div className="period-row period-row-5" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <span>SUMMA</span>
                  <span style={{ textAlign: 'right' }}>{Math.round(manadData.timSum).toLocaleString('sv-SE')}</span>
                  <span style={{ textAlign: 'right' }}>{kr(manadData.lonTot)}</span>
                  <span style={{ textAlign: 'right' }}>{kr(manadData.omsTot)}</span>
                  <span style={{ textAlign: 'right' }}>{manadData.omsTot > 1000 ? `${Math.round(manadData.lonTot / manadData.omsTot * 100)}%` : '–'}</span>
                </div>
              )}
              {manadData.enheter.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '8px 0' }}>Ingen data för månaden.</div>}
            </div>
            <div className="note">Urklickade avdelningar räknas bort ur alla summor på sidan. OH saknar omsättning per design.</div>
          </div>

          {/* Personer med anställningsform */}
          <div className="card">
            <div className="card-title">Personer — {mLbl}{sok.trim() ? ` · filter "${sok.trim()}"` : ''}</div>
            <div className="period-table">
              <div className="period-header period-row-6">
                <span>Namn</span><span>Form</span><span>Avdelning</span>
                <span style={{ textAlign: 'right' }}>Dagar</span>
                <span style={{ textAlign: 'right' }}>Timmar</span>
                <span style={{ textAlign: 'right' }}>Lönekostnad</span>
              </div>
              {manadData.personer.map((p, i) => {
                const enh = [...p.enheter];
                return (
                  <div key={i} className="period-row period-row-6" style={{ cursor: 'pointer' }}
                    onClick={() => { setValdPerson(p.namn); setMode('person'); setPFrom(manadFrom); setPTo(manadTo); setSok(''); }}
                    title="Visa personens dagar">
                    <span style={{ fontWeight: 600, color: COLOR }}>{p.namn}</span>
                    <span><KatBadge namn={p.namn} /></span>
                    <span style={{ color: enh.length === 1 ? enhetColor(enh[0]) : 'var(--text-secondary)', fontWeight: 500, fontSize: 11.5 }}>
                      {enh.length === 1 ? enh[0] : `${enh.length} avd.`}
                    </span>
                    <span style={{ textAlign: 'right' }}>{p.dagar.size}</span>
                    <span style={{ textAlign: 'right' }}>{p.tim > 0 ? Math.round(p.tim) : '–'}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>{kr(p.lon)}</span>
                  </div>
                );
              })}
              {manadData.personer.length > 0 && (
                <div className="period-row period-row-6" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <span>SUMMA ({manadData.personer.length} pers)</span><span /><span />
                  <span />
                  <span style={{ textAlign: 'right' }}>{Math.round(manadData.personer.reduce((s, p) => s + p.tim, 0)).toLocaleString('sv-SE')}</span>
                  <span style={{ textAlign: 'right' }}>{kr(manadData.personer.reduce((s, p) => s + p.lon, 0))}</span>
                </div>
              )}
              {manadData.personer.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '8px 0' }}>Inga personer {sok.trim() ? 'matchar sökningen' : 'denna månad'}.</div>}
            </div>
            <div className="note">
              Klicka <strong>Form</strong>-knappen för att växla Fast → Säsong → osatt. Valet sparas och gäller i alla månader. Klicka raden i övrigt för personens dagsdetaljer.
            </div>
          </div>
        </>
        );
      })()}

      {/* ── PERSON ── */}
      {!loading && !err && mode === 'person' && persData && (
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div className="card">
            <div className="card-title">Personer — {from} → {to}</div>
            <div className="period-table">
              <div className="period-header period-row-4">
                <span>Namn</span><span style={{ textAlign: 'right' }}>Dagar</span><span style={{ textAlign: 'right' }}>Timmar</span><span style={{ textAlign: 'right' }}>Lönekostnad</span>
              </div>
              {persData.lista.map((p, i) => (
                <div key={i} className="period-row period-row-4"
                  onClick={() => setValdPerson(p.namn)}
                  style={{ cursor: 'pointer', background: valdPerson === p.namn ? '#EAF0F6' : 'transparent' }}>
                  <span style={{ fontWeight: 600, color: COLOR }}>{p.namn}</span>
                  <span style={{ textAlign: 'right' }}>{p.dagar.size}</span>
                  <span style={{ textAlign: 'right' }}>{p.tim > 0 ? Math.round(p.tim) : '–'}</span>
                  <span style={{ textAlign: 'right', fontWeight: 600 }}>{kr(p.lon)}</span>
                </div>
              ))}
              {persData.lista.length > 0 && (
                <div className="period-row period-row-4" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <span>SUMMA ({persData.lista.length} pers)</span>
                  <span />
                  <span style={{ textAlign: 'right' }}>{Math.round(persData.listSum.tim).toLocaleString('sv-SE')}</span>
                  <span style={{ textAlign: 'right' }}>{kr(persData.listSum.lon)}</span>
                </div>
              )}
              {persData.lista.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '8px 0' }}>Inga personer {sok.trim() ? 'matchar sökningen' : 'i perioden'}.</div>}
            </div>
            <div className="note">Klicka på en person för dagsdetaljer. Endast stämplade löner (styrfilens fasta löner är inte personkopplade).</div>
          </div>

          <div className="card">
            {persData.vald ? (
              <>
                <div className="card-title">{persData.vald.namn} — {from} → {to}</div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 6, fontFamily: 'system-ui,sans-serif' }}>
                  <div><div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Lönekostnad</div><div style={{ fontSize: 20, fontWeight: 700 }}>{kr(persData.vald.lon)}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Timmar</div><div style={{ fontSize: 20, fontWeight: 700 }}>{tim1(persData.vald.tim)}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Dagar</div><div style={{ fontSize: 20, fontWeight: 700 }}>{persData.vald.dagar.size}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Kr / tim</div><div style={{ fontSize: 20, fontWeight: 700 }}>{persData.vald.tim > 0 ? Math.round(persData.vald.lon / persData.vald.tim).toLocaleString('sv-SE') : '–'}</div></div>
                </div>
                {/* Schablon-uppdelning av arbetsgivarkostnaden */}
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border)', fontFamily: 'system-ui,sans-serif' }}>
                  <div><div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Bruttolön (schablon)</div><div style={{ fontSize: 15, fontWeight: 600 }}>{kr(persData.vald.lon / (1 + AGA))}</div></div>
                  <div><div style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Arb.avgift 31,42% (schablon)</div><div style={{ fontSize: 15, fontWeight: 600 }}>{kr(persData.vald.lon - persData.vald.lon / (1 + AGA))}</div></div>
                  <div style={{ alignSelf: 'flex-end', fontSize: 11, color: 'var(--text-secondary)' }}>Qlik har endast total arbetsgivarkostnad — uppdelningen är schablonberäknad.</div>
                </div>
                <div className="period-table">
                  <div className="period-header period-row-4">
                    <span>Dag</span><span>Avdelning</span><span style={{ textAlign: 'right' }}>Timmar</span><span style={{ textAlign: 'right' }}>Lön</span>
                  </div>
                  {persData.valdRader.map((r, i) => (
                    <div key={i} className="period-row period-row-4">
                      <span>{fmtDag(r.datum)} <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{r.datum.slice(5)}</span></span>
                      <span style={{ color: enhetColor(r.enhet), fontWeight: 500 }}>{r.enhet}</span>
                      <span style={{ textAlign: 'right' }}>{r.tim > 0 ? r.tim.toFixed(2).replace('.', ',') : '–'}</span>
                      <span style={{ textAlign: 'right', fontWeight: 600 }}>{kr(r.lon)}</span>
                    </div>
                  ))}
                  {persData.valdRader.length > 0 && (
                    <div className="period-row period-row-4" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                      <span>SUMMA</span><span />
                      <span style={{ textAlign: 'right' }}>{tim1(persData.vald.tim)}</span>
                      <span style={{ textAlign: 'right' }}>{kr(persData.vald.lon)}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif', padding: '20px 0', textAlign: 'center' }}>
                ← Välj en person i listan för dagsdetaljer
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
